import { Injectable, OnDestroy, inject } from '@angular/core';

import { Observable, Subject } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { SharedLanguageService } from '@language';
import {
    Group,
    MultiFormSelectOptions,
    NotificationType,
    ShAbstractToastService,
    ShToastFactory,
    Toast,
    User,
    UserFunction,
    UserFunctions,
    UserPreferences
} from '@stylehaven/components';
import { StoreService } from '../../services/store/store.service';
import { UserStoreService } from '../../services/user/user-store.service';

@Injectable()
export abstract class BaseSandbox implements OnDestroy, ShAbstractToastService {
    readonly destroy$: Subject<void> = new Subject<void>();

    protected storeService: StoreService = inject(StoreService);
    protected userStoreService: UserStoreService = inject(UserStoreService);
    protected translateService: SharedLanguageService = inject(SharedLanguageService);
    protected toastFactory: ShToastFactory = inject(ShToastFactory);

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // #region Toasts
    public addToast(toast: Toast): void {
        this.storeService.addNewToast(toast);
    }

    public closeToast(toastId: number): void {
        this.storeService.deleteToast(toastId);
    }

    public updateToast(toastId: number, toast: Partial<Toast>): void {
        const updatedToast: Toast = this.toastFactory.create({ ...toast, id: toastId });
        this.storeService.addToast(updatedToast);
    }

    public createAndAddNewToast(
        messageToken: string,
        type: NotificationType,
        linkTextToken?: string,
        route?: string
    ): Toast {
        const toast: Toast = this.toastFactory.create({
            message: this.translate(messageToken),
            type,
            ...(route && linkTextToken && { navigationLink: { route }, linkText: this.translate(linkTextToken) })
        });

        this.addToast(toast);
        return toast;
    }
    // #endregion

    // #region Users
    public getCurrentUser(): Observable<User> {
        return this.userStoreService.getCurrentUser();
    }

    public getUserPreferences(): Observable<UserPreferences> {
        return this.userStoreService.getUserPreferences();
    }

    public getUserGroups(): Observable<Group[]> {
        return this.userStoreService.getUserGroups();
    }

    public getPermissionOptions(): Observable<MultiFormSelectOptions> {
        return this.userStoreService.getAssignableUserGroups().pipe(
            map(groups => {
                return {
                    options: groups.map(group => {
                        return { label: group.Name, value: group.Id };
                    }),
                    isLiveData: false
                } as MultiFormSelectOptions;
            }),
            shareReplay(1)
        );
    }

    public hasUserFunction(userFunction: UserFunction): Observable<boolean> {
        return this.userStoreService.hasUserFunction(userFunction);
    }

    public getUserFunctions(): Observable<UserFunctions> {
        return this.userStoreService.getUserFunctions();
    }
    // #endregion

    // #region Translation
    public translate(token: string, params?: Object | undefined): string {
        return this.translateService.instant(token, params);
    }
    // #endregion
}
